import { FC, useState } from 'react';
import classNames from 'classnames';
import { Link } from '@/components';
import startCase from 'lodash/startCase';

import { NOT_DEFINED } from '@/constants';
import { useAnalytics, useUiStore } from '@/data';
import { useCartLineDealBadge } from '@/hooks';
import { CartLineItem, GProduct } from '@/types';
import { currencyFormat, getBogoSpecialURL } from '@/utils';
import { CartItemBadges } from './CartItemBadges';

import {
  getUnitTierIsSpecial,
  getUnitTierPricing,
} from '../Product/context/utils';
import {
  Badge,
  Close,
  Counter,
  IconSvg,
  Image,
  Price,
  ProductProvider,
} from '@/components';

import style from './cartnavigation.module.scss';

interface CartBodyItemProps {
  product: CartLineItem;
}

const CartBodyItem: FC<CartBodyItemProps> = ({ product }) => {
  const [, setState] = useUiStore();
  const [closeBogo, setCloseBogo] = useState(false);
  const { measureProductClick } = useAnalytics();
  const { showBadge, isBundleOrBogoSpecial, badgeMeta } =
    useCartLineDealBadge(product);
  const bogo = badgeMeta ?? { discount_id: '', discount_title: '' };

  const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

  const trackingClick = (product: GProduct) =>
    measureProductClick(product, 'shopping cart');

  const v = product?.weightVariant;
  const unitTierPricing = product?.unitPricingTier
    ? getUnitTierPricing(product.unitPricingTier, product.pricing)
    : null;
  const isSpecial = unitTierPricing
    ? getUnitTierIsSpecial(product.unitPricingTier!, product.pricing)
    : (v?.salePrice ?? 0) < (v?.price ?? 0);
  const stockPhoto = product?.asset?.stock ?? false;

  const isBundleActive = isBundleOrBogoSpecial && showBadge;

  const brandName = product?.brand_name ?? NOT_DEFINED;
  const categoryName =
    startCase(product.category as string) || startCase(NOT_DEFINED);

  const price = (
    <Price
      price={`$${currencyFormat(v?.price!)}`}
      salePrice={`$${currencyFormat(
        unitTierPricing?.saleCents ?? v?.salePrice ?? 0,
      )}`}
      special={isSpecial}
    />
  );

  return (
    <li
      className={classNames(style.cart__product, {
        [style.cart__product_active_bogo]: isBundleActive && !closeBogo,
      })}
    >
      <ProductProvider product={product}>
        <div
          className={classNames(style.cart__product_body, {
            [style.cart__product_body_active_bogo]:
              isBundleActive && !closeBogo,
          })}
        >
          <Link
            className={style.cart__item_image}
            href={`${product.url}?item=${product.id}`}
            onClick={() => {
              trackingClick(product);
              setState({ is_cart_open: false });
            }}
          >
            <figure>
              <Image
                alt={product?.name ?? 'Product image'}
                fallback={`${REVERSE_PROXY_LINK}/images/product-placeholder.svg`}
                height={72}
                src={product?.asset?.image ?? ''}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
                width={72}
              />
            </figure>
            {stockPhoto && (
              <span className={style.product__in_stock}>Stock photo</span>
            )}
          </Link>
          <div className={style.cart__detail_item}>
            <div className={style.cart__detail_brand}>
              <p>
                <span>{brandName}</span> • <span>{categoryName}</span>
              </p>
              <CartItemBadges
                badgeClass={style.cart__product_badge}
                containerClass={style.cart__product_badges}
                product={product}
              />
            </div>
            <Link
              className={style.cart__info_container}
              href={`${product.url}?item=${product.id}`}
              onClick={() => {
                trackingClick(product);
                setState({
                  is_cart_open: false,
                });
              }}
            >
              <p
                className={classNames(
                  'heading',
                  'heading_level-4',
                  style.heading_level_4,
                )}
                title={product.name!}
              >
                {product.name}
              </p>
              <div className={style.price__container}>{price}</div>
            </Link>
            <div className={style.actions}>
              <Counter onShopping product={product} />
            </div>
          </div>
        </div>
        {isBundleActive && !closeBogo && badgeMeta && (
          <Badge className={style.cart_item_bogo} href="/" key={product.id}>
            <figure>
              <IconSvg aria-hidden="true" name="gift" />
            </figure>
            <div className={style.cart_item_bogo_message}>
              <span>You are on your way to a Deal!</span>
              <Link
                href={getBogoSpecialURL(bogo)}
                onClick={() => {
                  setState({ is_cart_open: false });
                }}
              >
                Click here to complete
              </Link>
            </div>
            <Close
              aria-label="Close bundle deal notification"
              className={style.cart_item_bogo_close}
              onClick={() => {
                setCloseBogo(true);
              }}
            />
          </Badge>
        )}
      </ProductProvider>
    </li>
  );
};

export { CartBodyItem };
